import React from 'react'
import { Pause, SkipForward, Plus } from 'lucide-react'
import { Badge } from '../ui/badge'
import {
  agruparPorTipo,
  formatearDuracion,
  calcularResumen
} from '../../lib/training/recorridoUtils'
import { getSeccionTipoColor } from '../../constants/seccionTipos'

const ResumenRecorrido = ({ recorrido, compacto = false }) => {
  if (!recorrido) {
    return (
      <div className="text-sm text-muted-foreground italic">
        Sin recorrido registrado para esta sesion.
      </div>
    )
  }

  const grupos = agruparPorTipo(recorrido.secciones)
  const { saltadas, extendidas, diferenciaPlaneadoSeg } = calcularResumen(recorrido)
  const diferenciaPositiva = diferenciaPlaneadoSeg >= 0

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-md bg-muted/50 px-3 py-2">
          <p className="text-xs text-muted-foreground">Duración total</p>
          <p className="text-base font-semibold text-foreground">
            {formatearDuracion(recorrido.duracion_total_real_seg)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            (planeado: {formatearDuracion(recorrido.duracion_total_planeada_seg)})
          </p>
        </div>
        <div className="rounded-md bg-muted/50 px-3 py-2">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Pause className="h-3 w-3" /> Pausas
          </p>
          <p className="text-base font-semibold text-foreground">
            {formatearDuracion(recorrido.pausas_total_seg)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {diferenciaPositiva ? '+' : ''}{formatearDuracion(Math.abs(diferenciaPlaneadoSeg))} vs planeado
          </p>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-foreground mb-2">Distribucion del tiempo</p>
        <div className="space-y-1.5">
          {grupos.map((g) => {
            const colorClasses = getSeccionTipoColor(g.tipo).split(' ')
            const bgClass = (colorClasses.find((c) => c.startsWith('bg-')) || 'bg-muted').replace('100', '500')
            return (
              <div key={g.tipo} className="flex items-center gap-2 text-xs">
                <span className="w-32 truncate">{g.label}</span>
                <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${bgClass}`}
                    style={{ width: `${g.porcentaje}%` }}
                  />
                </div>
                <span className="w-16 text-right text-muted-foreground tabular-nums">
                  {formatearDuracion(g.duracionReal)}
                </span>
                <span className="w-10 text-right text-muted-foreground tabular-nums">{g.porcentaje}%</span>
              </div>
            )
          })}
        </div>
      </div>

      {(saltadas.length > 0 || extendidas.length > 0) && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Cambios durante el entrenamiento</p>
          {saltadas.map((s) => (
            <div key={`saltada-${s.nombre}`} className="flex items-center gap-2 text-xs text-foreground">
              <SkipForward className="h-3.5 w-3.5 text-amber-600" />
              <span><strong>{s.nombre}:</strong> saltada</span>
            </div>
          ))}
          {extendidas.map((s) => {
            const extra = s.duracion_real_seg - s.duracion_planeada_seg
            return (
              <div key={`ext-${s.nombre}`} className="flex items-center gap-2 text-xs text-foreground">
                <Plus className="h-3.5 w-3.5 text-blue-600" />
                <span>
                  <strong>{s.nombre}:</strong> +{formatearDuracion(extra)} extendida
                </span>
              </div>
            )
          })}
        </div>
      )}

      {!compacto && (
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-sm font-medium text-foreground">Detalle por seccion</p>
          {(recorrido.secciones || []).map((s, idx) => (
            <div key={idx} className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded-md hover:bg-muted/50">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-muted-foreground tabular-nums w-5">{idx + 1}</span>
                <span className={`truncate ${s.fue_saltada ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                  {s.nombre}
                </span>
                <Badge variant="outline" className={`text-[9px] ${getSeccionTipoColor(s.tipo)}`}>
                  {s.tipo}
                </Badge>
              </div>
              <span className="text-muted-foreground tabular-nums">
                {formatearDuracion(s.duracion_real_seg)} / {formatearDuracion(s.duracion_planeada_seg)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ResumenRecorrido
